import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight, FileText, LayoutList } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { api } from '../../context/AuthContext';
import { ErfassungVorschau, Gross, blobLaden } from './ErfassungVorschau';
import { KiZusammenfassung } from './Ki';
import { AnlageHerkunft } from './AnlageHerkunft';

const d = (iso) => { if (!iso) return ''; const x = new Date(iso); return Number.isNaN(x.getTime()) ? iso : x.toLocaleDateString('de-DE'); };
const ja = (v) => (v === true ? 'Ja' : v === false ? 'Nein' : v);
const FAM = { ledig: 'Ledig', verheiratet: 'Verheiratet', eingetragene_lebenspartnerschaft: 'Eingetragene Lebenspartnerschaft', geschieden: 'Geschieden', getrennt_lebend: 'Getrennt lebend', verwitwet: 'Verwitwet' };

const abschnitte = (a) => [
  ['Stammdaten', [['Typ', a.type === 'unternehmen' ? 'Unternehmen' : a.type === 'privat' ? 'Privatperson' : null], ['Anrede', a.salutation], ['Vorname', a.firstName], ['Nachname', a.lastName], ['Firma', a.companyName], ['Rechtsform', a.legalForm], ['Geburtsdatum', d(a.birthDate)]]],
  ['Kontakt', [['E-Mail', a.email], ['Telefon', a.phone], ['Adresse', [a.street, [a.zip, a.city].filter(Boolean).join(' '), a.country].filter(Boolean).join(', ')]]],
  ['Anliegen', [['Rechtsgebiet', a.rechtsgebiet], ['Gegner', a.gegner], ['Behörde', a.behoerde], ['Frist läuft', a.frist?.laeuft ? `Ja${a.frist.frist_datum ? ` · bis ${d(a.frist.frist_datum)}` : ''}${a.frist.bescheid_datum ? ` · Bescheid vom ${d(a.frist.bescheid_datum)}` : ''}` : null]]],
  ['Steuer & Bank', [['Steuer-ID', a.taxId], ['USt-IdNr.', a.ustId], ['IBAN', a.iban], ['Vorheriger Steuerberater', a.previousTaxAdvisor], ['Erwarteter Jahresumsatz', a.expectedAnnualRevenue]]],
  ['Familie', [['Familienstand', FAM[a.familienstand] || a.familienstand], ['Ehepartner/in', a.spouseName], ['Geburtsdatum Partner', d(a.spouseBirthDate)],
    ['Kinder', (a.children || []).map((c) => `${c.name || '—'}${c.birthDate ? ` (${d(c.birthDate)})` : ''}`).join(', ')]]],
  ['Rechtsschutz', [['Versicherung', ja(a.hasLegalInsurance)], ['Gesellschaft', a.insuranceCompany], ['Versicherungsnummer', a.insurancePolicyNumber]]],
  ['Unternehmen', [['Gesellschafter', (a.shareholders || []).map((s) => s.name).filter(Boolean).join(', ')], ['Geschäftsführer', (a.directors || []).map((s) => s.name).filter(Boolean).join(', ')]]],
  ['Zustimmung', [['AGB', ja(a.agb)], ['Datenschutz', ja(a.datenschutz)], ['E-Mail bestätigt', ja(a.email_bestaetigt)], ['Portalzugang gewünscht', ja(a.portal_zugang)], ['Eingegangen', d(a.created_at)]]],
].map(([t, f]) => [t, f.filter(([, v]) => v !== undefined && v !== null && v !== '')]).filter(([, f]) => f.length);

const Felder = ({ a, onOeffnen }) => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5" data-testid="erfassung-detail-inhalt">
    <KiZusammenfassung anfrageId={a.anfrage_id} />
    {abschnitte(a).map(([titel, felder]) => (
      <section key={titel}>
        <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d] mb-2">{titel}</h4>
        <dl className="space-y-1.5">{felder.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[130px_1fr] gap-2 text-sm"><dt className="text-gray-500 text-xs pt-0.5">{k}</dt><dd className="text-[#1B3A5C] break-words">{v}</dd></div>))}</dl>
      </section>
    ))}
    {a.description && (
      <section className="sm:col-span-2">
        <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d] mb-2">Beschreibung</h4>
        <p className="text-sm text-[#1B3A5C] whitespace-pre-wrap bg-[#fbf7f0] border-l-2 border-[#b8893d] p-3 rounded-sm" data-testid="erfassung-detail-beschreibung">{a.description}</p>
      </section>
    )}
    <ErfassungVorschau a={a} onOeffnen={onOeffnen} />
  </div>
);

const usePdf = (a, aktiv) => {
  const [pdf, setPdf] = useState(null);
  useEffect(() => {
    if (!a || !aktiv || pdf) return undefined;
    let ok = true;
    blobLaden(`/admin/anfragen/${a.anfrage_id}/pdf`).then((b) => ok && setPdf({ label: 'Erfassungsbogen (PDF)', blob: b, url: URL.createObjectURL(b), art: 'pdf', name: `Mandantenerfassung_${a.aktenzeichen || a.anfrage_id}.pdf` }))
      .catch(() => ok && setPdf({ fehler: true }));
    return () => { ok = false; };
  }, [a, aktiv, pdf]);
  useEffect(() => () => { if (pdf?.url) URL.revokeObjectURL(pdf.url); }, [pdf]);
  return pdf;
};

const TABS = [['felder', 'Felder & Dokumente', LayoutList], ['pdf', 'PDF-Ansicht', FileText]];

export const ErfassungDetailDialog = ({ anfrageId, onClose }) => {
  const [a, setA] = useState(null);
  const [fehler, setFehler] = useState('');
  const [tab, setTab] = useState('felder');
  const [gross, setGross] = useState(null);
  const pdf = usePdf(a, tab === 'pdf');
  useEffect(() => {
    let aktuell = true;
    setA(null); setFehler('');
    api.get(`/admin/anfragen/${anfrageId}`)
      .then((r) => { if (aktuell) setA(r.data); })
      .catch((e) => { if (aktuell) setFehler(e?.response?.data?.detail || 'Erfassung konnte nicht geladen werden'); });
    return () => { aktuell = false; };
  }, [anfrageId]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="erfassung-detail-dialog">
        <DialogTitle className="font-serif text-2xl text-[#1B3A5C]">{a ? `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Mandantenerfassung' : 'Mandantenerfassung'}</DialogTitle>
        <DialogDescription>{a ? `${a.rechtsgebiet || 'Ohne Rechtsgebiet'} · eingegangen am ${d(a.created_at)}${a.aktenzeichen ? ` · Az. ${a.aktenzeichen}` : ''}` : 'Details der Erfassung'}</DialogDescription>
        {fehler && <p className="text-sm text-rose-700" data-testid="erfassung-detail-fehler">{fehler}</p>}
        {!a && !fehler && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>}
        {a && (
          <>
            <AnlageHerkunft caseId={a.anfrage_id} />
            <div className="flex gap-1 border-b border-gray-100 -mt-1">
              {TABS.map(([k, l, I]) => (
                <button key={k} type="button" onClick={() => { setTab(k); setGross(null); }} data-testid={`erfassung-tab-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 -mb-px transition-colors duration-200 ${tab === k ? 'border-[#b8893d] text-[#1B3A5C]' : 'border-transparent text-gray-500 hover:text-[#1B3A5C]'}`}><I className="w-3.5 h-3.5" />{l}</button>
              ))}
            </div>
            {gross ? <Gross f={gross} onClose={() => setGross(null)} />
              : tab === 'felder' ? <Felder a={a} onOeffnen={setGross} />
              : !pdf ? <div className="h-[70vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>
              : pdf.fehler ? <p className="text-sm text-rose-700" data-testid="erfassung-pdf-fehler">PDF konnte nicht erzeugt werden.</p>
              : <div data-testid="erfassung-pdf-vorschau"><Gross f={pdf} onClose={() => setTab('felder')} /></div>}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
              <Link to={`/team-portal/${a.anfrage_id}`} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-[10px] text-xs border border-gray-300 hover:border-[#1B3A5C] text-[#1B3A5C] transition-colors" data-testid="erfassung-detail-akte">Akte öffnen <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ErfassungDetailDialog;
